'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { LmsUser } from '@/types/moodle.types';
import { lmsClient } from '@/lib/api/lmsClient';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { LMS_EVENTS } from '@/lib/analytics';

const USER_INFO_STORAGE_KEY = 'lms_user_info';
const BEARER_TOKEN_STORAGE_KEY = 'lms_token';

interface AuthContextType {
  user: LmsUser | null;
  login: (user: LmsUser) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<LmsUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { identify, track, reset } = useAnalytics();

  useEffect(() => {
    // We only store public info (name, id) for UI display.
    // Auth is handled by HttpOnly lms_token cookie.
    const stored = localStorage.getItem(USER_INFO_STORAGE_KEY);
    if (stored) {
      try {
        const parsed: LmsUser = JSON.parse(stored);
        setUser(parsed);
        // Re-identify on page reload so the session is linked to the user
        identify(`moodle_${parsed.userid}`, {
          username: parsed.username,
          fullname: parsed.fullname,
        });
      } catch (e) {
        console.error('[AuthContext] Failed to restore session:', e);
      }
    }
    setIsLoading(false);
  }, []);

  const login = (u: LmsUser & { token?: string }) => {
    if (u.token) {
      localStorage.setItem(BEARER_TOKEN_STORAGE_KEY, u.token);
      // Set client-accessible cookie for Next.js route guard middleware
      document.cookie = `lms_token=${u.token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
    }
    localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(u));
    setUser(u);
    identify(`moodle_${u.userid}`, { username: u.username, fullname: u.fullname });
    track(LMS_EVENTS.USER_LOGGED_IN, { userId: u.userid, username: u.username });
  };

  const logout = async () => {
    track(LMS_EVENTS.USER_LOGGED_OUT);
    try {
      await lmsClient.auth.logout();
    } catch (err) {
      console.error('Failed to log out cleanly on server:', err);
    } finally {
      reset();
      localStorage.removeItem(BEARER_TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_INFO_STORAGE_KEY);
      // Clear the cookie
      document.cookie = 'lms_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      setUser(null);
      window.location.href = '/courses';
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
